/**
 * Parses changelog entry markdown files (frontmatter + body) without external deps.
 *
 * Supported frontmatter shape — key: value pairs, with array support via [a, b, c]:
 *   ---
 *   title: One-line summary
 *   date: 2026-04-25
 *   audience: sales
 *   category: feature
 *   products: [legal-opinion, admin]     # see ChangelogProduct for the full set
 *   trello: https://trello.com/c/abc123
 *   video: /notifications/media/<uuid>.mp4      # optional walkthrough clip
 *   videoExpires: 2026-12-01                    # optional; hides the player from this date
 *   ---
 *
 * Anything richer (nested objects, multi-line strings) is intentionally not supported —
 * the schema is small and fixed; if it grows, swap to gray-matter.
 *
 * NOTE for anyone adding a key: `coerceFrontmatter` builds a FRESH object from
 * named fields with no spread, so a key added to an entry but not to that
 * function is parsed and then silently discarded. That silence is how
 * `category: change` and `products: [broker]` reached the corpus.
 */

import { isVideoMediaPath } from '../constants/notificationMediaTypes';

export type ChangelogAudience = 'sales' | 'all';

/**
 * The ONE list of each enum, as a runtime array with the type derived from it.
 *
 * A TS union has no runtime form, so anything that needs to VALIDATE an entry
 * — the coercer below, the on-disk contract test — would otherwise re-list the
 * members by hand and drift. Add a member here and every consumer sees it.
 */
export const CHANGELOG_CATEGORIES = [
  'feature',
  'improvement',
  'fix',
  'removed',
  'infra',
] as const;
export type ChangelogCategory = (typeof CHANGELOG_CATEGORIES)[number];
Object.freeze(CHANGELOG_CATEGORIES);

export const CHANGELOG_PRODUCTS = [
  'legal-opinion',
  'admin',
  'ai-scribe-frontend',
  'ai-scribe-admin',
  'broker-ai',
  'raf-frontend',
  'all',
] as const;
export type ChangelogProduct = (typeof CHANGELOG_PRODUCTS)[number];
Object.freeze(CHANGELOG_PRODUCTS);

/** Frontmatter keys `coerceFrontmatter` reads. Anything else is discarded. */
export const CHANGELOG_FRONTMATTER_KEYS = [
  'title',
  'date',
  'audience',
  'category',
  'products',
  'trello',
  'video',
  'videoExpires',
] as const;
Object.freeze(CHANGELOG_FRONTMATTER_KEYS);

export interface ChangelogFrontmatter {
  title: string;
  date: string;
  audience: ChangelogAudience;
  category: ChangelogCategory;
  products: ChangelogProduct[];
  trello?: string;
  /** Upload-produced walkthrough clip path, e.g. `/notifications/media/<uuid>.mp4`. */
  video?: string;
  /** `YYYY-MM-DD`. From this date the clip stops being SHOWN; the file is not deleted. */
  videoExpires?: string;
}

export interface ChangelogEntry {
  slug: string;
  frontmatter: ChangelogFrontmatter;
  body: string;
}

const FRONTMATTER_RE = /^---\s*\n([\s\S]*?)\n---\s*\n?([\s\S]*)$/;

/** `videoExpires` is compared as a string, so the format has to be exact. */
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A real calendar date in `YYYY-MM-DD`, not merely something shaped like one.
 *
 * The shape check alone admits `2026-13-01` and `2026-00-01`, and because the
 * comparison downstream is lexical rather than chronological they fail in
 * opposite directions: `2026-00-01` sorts below every real date so the clip
 * reads as expired immediately, while `2026-13-01` outranks every 2026 date so
 * the clip runs until January 2027. Round-tripping through Date rejects both,
 * and also catches 2026-02-30, which no range check on the digits would.
 */
function isCalendarDate(value: string): boolean {
  if (!ISO_DATE_RE.test(value)) return false;
  const [y, m, d] = value.split('-').map(Number);
  // Month is 0-indexed; a Date built from an out-of-range part rolls over into
  // another month, so comparing the parts back is what detects the rollover.
  const date = new Date(Date.UTC(y, m - 1, d));
  return (
    date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d
  );
}

function parseScalar(raw: string): string {
  const trimmed = raw.trim();
  if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
    return trimmed.slice(1, -1);
  }
  return trimmed;
}

function parseArray(raw: string): string[] {
  const inner = raw.trim().replace(/^\[|\]$/g, '');
  if (!inner.trim()) return [];
  return inner.split(',').map((s) => parseScalar(s));
}

function parseFrontmatter(text: string): Record<string, string | string[]> {
  const out: Record<string, string | string[]> = {};
  for (const line of text.split('\n')) {
    const stripped = line.replace(/#.*$/, '').trim();
    if (!stripped) continue;
    const sep = stripped.indexOf(':');
    if (sep === -1) continue;
    const key = stripped.slice(0, sep).trim();
    const valueRaw = stripped.slice(sep + 1).trim();
    if (valueRaw.startsWith('[')) {
      out[key] = parseArray(valueRaw);
    } else {
      out[key] = parseScalar(valueRaw);
    }
  }
  return out;
}

function coerceFrontmatter(parsed: Record<string, string | string[]>, slug: string): ChangelogFrontmatter {
  const title = String(parsed.title || slug);
  const date = String(parsed.date || '');
  const audience = (parsed.audience === 'all' ? 'all' : 'sales') as ChangelogAudience;
  const rawCategory = String(parsed.category || 'improvement');
  const category = ((CHANGELOG_CATEGORIES as readonly string[]).includes(rawCategory)
    ? rawCategory
    : 'improvement') as ChangelogCategory;
  const productsRaw = Array.isArray(parsed.products) ? parsed.products : [];
  // FILTER, don't cast. The category check above is an Array.includes scan, so
  // '__proto__' is simply not a member and falls back safely. `products`
  // downstream is an OBJECT INDEX (`PRODUCT_LABELS[p]`), which does walk the
  // prototype chain: products: [__proto__] would hand Object.prototype to React
  // as a child and take the entire page down for every admin, not just that
  // card. An unknown product is dropped rather than rendered.
  const products = productsRaw.filter((p): p is ChangelogProduct =>
    (CHANGELOG_PRODUCTS as readonly string[]).includes(p)
  );
  // Same reasoning as the media keys below: this value becomes a <MuiLink
  // href>, so a non-http(s) scheme (javascript:, data:) must not survive
  // parsing. React 19 warns on a javascript: href but still renders it.
  const trelloRaw = parsed.trello ? String(parsed.trello) : '';
  const trello = /^https?:\/\//i.test(trelloRaw) ? trelloRaw : undefined;
  // Both media keys are dropped unless well-formed. A malformed value must fail
  // the same quiet way an unknown key does rather than reaching a <video src>:
  // the path shape is the only thing stopping a stored value pointing
  // off-platform, so "close enough" is not a state worth rendering.
  const videoRaw = String(parsed.video ?? '');
  const video = isVideoMediaPath(videoRaw) ? videoRaw : undefined;
  const videoExpiresRaw = String(parsed.videoExpires ?? '');
  const videoExpires = isCalendarDate(videoExpiresRaw) ? videoExpiresRaw : undefined;
  return { title, date, audience, category, products, trello, video, videoExpires };
}

export function parseEntry(raw: string, filePath: string): ChangelogEntry | null {
  const match = raw.match(FRONTMATTER_RE);
  if (!match) return null;
  const [, frontText, body] = match;

  // Slug = filename without date prefix and .md suffix.
  const filename = filePath.split('/').pop() || filePath;
  const slug = filename.replace(/\.md$/, '');

  const parsed = parseFrontmatter(frontText);
  // Reject entries lacking the two load-bearing fields. Check the parsed map
  // directly — coerceFrontmatter falls back to the slug for title, which would
  // mask a missing one.
  if (!parsed.title || !parsed.date) return null;

  const frontmatter = coerceFrontmatter(parsed, slug);
  return { slug, frontmatter, body: body.trim() };
}

export function loadAllEntries(modules: Record<string, string>): ChangelogEntry[] {
  const entries: ChangelogEntry[] = [];
  for (const [filePath, raw] of Object.entries(modules)) {
    const entry = parseEntry(raw, filePath);
    if (entry) entries.push(entry);
  }
  // Newest first, ties broken by slug.
  entries.sort((a, b) => {
    const cmp = b.frontmatter.date.localeCompare(a.frontmatter.date);
    if (cmp !== 0) return cmp;
    return b.slug.localeCompare(a.slug);
  });
  return entries;
}
